import { Component, EventEmitter, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DwButtonCommand, DwButtonConfig, DwConfigService, DwConfigServiceToken, DwDateRangeInputComponent, DwModalConfig, DwModalHandler, DwModalResult, SuppressErrorMessagesHeader, DwMessage, DwMessageSeverity } from '@devwareapps/devware-cap';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';

// Keep CAP's dictionary field names intact; normalize DTO keys for either host serializer.
function normalize(value: any): any {
    if (Array.isArray(value)) { return value.map(normalize); }
    if (value && typeof value === 'object') {
        const result = {};
        Object.keys(value).forEach(key => result[key.charAt(0).toLowerCase() + key.slice(1)] = normalize(value[key]));
        return result;
    }
    return value;
}

@Component({
    selector: 'app-create-challenge-modal',
    templateUrl: './create-challenge-modal.component.html',
})
export class CreateChallengeModalComponent implements DwModalHandler, OnInit {
    modalConfig: DwModalConfig<any>;
    closeModal: EventEmitter<DwModalResult<any>>;
    model: any;
    challenges: any[] = [];
    products: any[] = [];
    busy = false;
    error = '';
    private readonly handledErrorHeaders = { [SuppressErrorMessagesHeader]: 'true' };
    get pageMessage(): DwMessage {
        return this.error ? { messageBody: this.error, severity: DwMessageSeverity.error, allowClose: false } : null;
    }
    created: any;
    @ViewChildren(DwDateRangeInputComponent) rangeControls: QueryList<DwDateRangeInputComponent>;
    readonly dateRanges = [
        { key: 'store', label: 'Store dates', start: 'storeStartDate', end: 'storeEndDate' },
        { key: 'registration', label: 'Registration dates', start: 'registrationStartDate', end: 'registrationEndDate' },
        { key: 'challenge', label: 'Challenge dates', start: 'challengeStartDate', end: 'challengeEndDate' },
    ];
    readonly dates = [
        { key: 'storeStartDate', label: 'Store start' }, { key: 'storeEndDate', label: 'Store end' },
        { key: 'registrationStartDate', label: 'Registration start' }, { key: 'registrationEndDate', label: 'Registration end' },
        { key: 'challengeStartDate', label: 'Challenge start' }, { key: 'challengeEndDate', label: 'Challenge end' },
    ];
    constructor(private http: HttpClient, @Inject(DwConfigServiceToken) private config: DwConfigService) {}
    private url(path: string): string { return this.config.coreConfig.apiRoot.replace(/\/$/, '') + '/fitness/challenges/' + path; }
    ngOnInit(): void { this.load(); }
    load(referenceId?: number): void {
        if (this.busy) { return; }
        this.busy = true; this.error = '';
        this.http.get(this.url('creation-options') + (referenceId ? '?referenceId=' + referenceId : ''), { headers: this.handledErrorHeaders }).pipe(
            take(1), map(normalize), finalize(() => this.busy = false),
        ).subscribe(options => {
            this.challenges = options.challenges; this.products = options.products;
            const previous = this.model;
            this.model = options.defaults;
            this.dates.forEach(d => this.model[d.key] = this.model[d.key].substring(0, 10));
            // Changing the source changes only source-dependent selections, not entered details.
            if (previous) {
                ['name', 'makeCurrent', ...this.dates.map(d => d.key)].forEach(key => this.model[key] = previous[key]);
            }
            this.model.shirtProductId = this.byType('Gear').length === 1 ? this.byType('Gear')[0].productId : null;
            this.model.donationProductId = this.byType('Donation').length === 1 ? this.byType('Donation')[0].productId : null;
            // This CAP version does not initialize its display/calendar from input changes.
            // Wait until Angular creates the controls, then initialize through their public API.
            setTimeout(() => this.rangeControls.forEach((control, index) => {
                const range = this.dateRanges[index];
                const start = moment(this.model[range.start], 'YYYY-MM-DD', true).format('M/D/YYYY');
                const end = moment(this.model[range.end], 'YYYY-MM-DD', true).format('M/D/YYYY');
                control.updateFromText(start + ' - ' + end);
            }));
        }, error => this.error = this.message(error));
    }
    dateChanged(key: string, value: string): void {
        if (this.busy || this.created) { return; }
        const date = moment(value, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD'], true);
        this.model[key] = date.isValid() ? date.format('YYYY-MM-DD') : null;
    }
    byType(type: string): any[] { return this.products.filter(p => (p.type || '').toLowerCase() === type.toLowerCase()); }
    registrationChanged(): void {
        const product = this.products.find(p => p.productId === this.model.registrationProductId);
        if (product) { this.model.registrationPrice = product.price; }
    }
    private message(error: any): string { return error.error && (error.error.title || error.error.Title) || 'Unable to complete the request. Try again.'; }
    buttonClick(button: DwButtonConfig): Observable<DwModalResult> {
        const result = new DwModalResult(); result.buttonCommand = button.command;
        result.closeModal = !this.busy; result.cancelled = true;
        if (this.busy) { return of(result); }
        if (button.command !== DwButtonCommand.saveAndClose) { return of(result); }
        result.cancelled = false; result.closeModal = false; this.error = '';
        const m = this.model;
        if (!m || !m.name || !m.name.trim() || !m.referenceChallengeId || !m.registrationProductId ||
            m.registrationPrice == null || m.registrationPrice < 0 || this.dates.some(d => !m[d.key])) {
            this.error = 'Complete the name, dates, registration product, and registration price.'; return of(result);
        }
        this.busy = true;
        const save = this.created ? of(this.created) : this.http.post(this.url('create'), m, { headers: this.handledErrorHeaders }).pipe(map(normalize));
        return save.pipe(take(1), switchMap(value => {
            this.created = value;
            return this.http.delete(this.config.coreConfig.apiRoot.replace(/\/$/, '') + '/general/cache', { responseType: 'text', headers: this.handledErrorHeaders });
        }), map(() => {
            result.data = this.created; result.closeModal = true; return result;
        }), catchError(error => {
            this.error = this.created ? 'Challenge created, but cache clearing failed. Press Ok to retry cache clearing.' : this.message(error);
            return of(result);
        }), finalize(() => this.busy = false));
    }
}
